import { animate, state, style, transition, trigger } from '@angular/animations';

import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, inject, Input, OnDestroy, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomContextMenuItem } from '../custom-context-menu/custom-context-menu.component';
import { ContextMenuSelection, CustomContextMenuService } from '../custom-context-menu/custom-context-menu.service';

@Component({
  selector: 'app-collapsible-list-item-group',
  templateUrl: './collapsible-list-item-group.component.html',
  styleUrls: ['./collapsible-list-item-group.component.scss'],
  standalone: true,
  imports: [MatIconModule, MatButtonModule, MatTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('collapseExpand', [
      state('collapsed', style({ height: '0px', overflow: 'hidden', opacity: 0 })),
      state('expanded', style({ height: '*', overflow: 'hidden', opacity: 1 })),
      transition('expanded <=> collapsed', [animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')]),
    ]),
  ],
})
export class CollapsibleListItemGroupComponent implements OnDestroy {
  @Input() groupText: string = '';
  @Input() groupIcon?: string;
  @Input() isSvgIcon: boolean = false;
  @Input() data?: any;
  @Input() initiallyExpanded: boolean = true;
  @Input() buttons: CustomContextMenuItem[] = [];
  @Input() isGlobalDragActive: boolean = false;

  @Output() groupClicked = new EventEmitter<null>();
  @Output() optionsClicked = new EventEmitter<MouseEvent>();
  @Output() addClicked = new EventEmitter<MouseEvent>();
  @Output() contextAction = new EventEmitter<{ actionId: string; data?: any }>();

  @ViewChild('groupTextElement') groupTextElementRef!: ElementRef<HTMLElement>;

  isExpanded: WritableSignal<boolean>;

  private contextMenuService = inject(CustomContextMenuService);
  private dragHoverTimeout: any;

  constructor() {
    this.isExpanded = signal(this.initiallyExpanded);
  }

  ngOnInit(): void {
    this.isExpanded.set(this.initiallyExpanded);
  }

  toggleExpand(event: MouseEvent): void {
    event.stopPropagation();
    this.isExpanded.update((expanded) => !expanded);
  }

  onGroupHeaderClick(): void {
    this.groupClicked.emit();
  }

  onGroupHeaderEnter(): void {
    if (this.isGlobalDragActive) {
      this.dragHoverTimeout = setTimeout(() => {
        if (!this.isExpanded()) {
          this.isExpanded.set(true);
        }
      }, 400); // 400ms delay to prevent accidental expansions while just passing through
    }
  }

  onGroupHeaderLeave(): void {
    if (this.dragHoverTimeout) {
      clearTimeout(this.dragHoverTimeout);
      this.dragHoverTimeout = null;
    }
  }

  onGroupOptionsClick(event: MouseEvent, triggerElement: MatIconButton): void {
    event.stopPropagation();
    if (triggerElement && triggerElement._elementRef) {
      this.contextMenuService.open(triggerElement._elementRef, this.buttons, this.data).subscribe((selection: ContextMenuSelection) => {
        if (selection) {
          this.handleContextMenuItemSelection(selection);
        }
      });
    }
  }

  onGroupAddClick(event: MouseEvent): void {
    event.stopPropagation();
    this.addClicked.emit(event);
  }

  private handleContextMenuItemSelection(selection: ContextMenuSelection): void {
    this.contextAction.emit({ actionId: selection.actionId, data: selection.data });
  }

  ngOnDestroy(): void {
    this.contextMenuService.close();
  }
}
